/**
 * KpiTrendChart — grote trendgrafiek voor één metric over de volle historie.
 * Historische punten (source='excel-historisch') worden gedempt en gestippeld
 * getoond; op de overgang delen beide reeksen één punt zodat de lijn
 * doorloopt. Licht thema, office-app design-tokens, NL-notatie.
 */

import { useMemo } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  formatMetricValue,
  formatPointLabel,
  isRunningPeriod,
  type Grain,
  type MetricFormat,
  type Series,
} from '../../stores/cijfers-store';

interface ChartRow {
  period: string;
  label: string;
  live: number | null;
  hist: number | null;
}

/** Dimensies per periode sommeren; live wint als bron-typering van het punt. */
function toRows(series: Series, grain: Grain): ChartRow[] {
  const byPeriod = new Map<string, { value: number; live: boolean }>();
  for (const p of series.points) {
    if (p.value === null) continue;
    const cur = byPeriod.get(p.period) ?? { value: 0, live: false };
    cur.value += p.value;
    cur.live = cur.live || p.source === 'live';
    byPeriod.set(p.period, cur);
  }
  const rows: ChartRow[] = [...byPeriod.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([period, { value, live }]) => ({
      period,
      label: formatPointLabel(period, grain),
      live: live ? value : null,
      hist: live ? null : value,
    }));
  // Verbind de reeksen op de overgang historisch → live.
  for (let i = 1; i < rows.length; i += 1) {
    if (rows[i].live !== null && rows[i - 1].hist !== null && rows[i - 1].live === null) {
      rows[i - 1].live = rows[i - 1].hist;
    }
  }
  // De lopende (onvolledige) periode plot als een duik naar bijna-nul —
  // misleidend in een trendlijn. Weglaten, tenzij de reeks anders (vrijwel)
  // leeg is (telefonie begint pas in de lopende periode).
  if (rows.length > 2 && isRunningPeriod(rows[rows.length - 1].period, grain)) {
    rows.pop();
  }
  return rows;
}

function compactValue(value: number, format: MetricFormat): string {
  if (format === 'percent') return `${Math.round(value * 100)}%`;
  const abs = Math.abs(value);
  const prefix = format.startsWith('currency') ? '€ ' : '';
  if (abs >= 1_000_000) return `${prefix}${(value / 1_000_000).toLocaleString('nl-NL', { maximumFractionDigits: 1 })}M`;
  if (abs >= 10_000) return `${prefix}${Math.round(value / 1000)}k`;
  return `${prefix}${value.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}`;
}

interface KpiTrendChartProps {
  series: Series;
  grain: Grain;
  height?: number;
}

export function KpiTrendChart({ series, grain, height = 280 }: KpiTrendChartProps) {
  const rows = useMemo(() => toRows(series, grain), [series, grain]);
  const hasHist = rows.some((r) => r.hist !== null);

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-gray-400" style={{ height }}>
        Geen data voor deze periode-indeling.
      </div>
    );
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: '#6b7280', fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            minTickGap={24}
          />
          <YAxis
            tick={{ fill: '#6b7280', fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={56}
            tickFormatter={(v: number) => compactValue(v, series.format)}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              fontSize: 12,
            }}
            formatter={(value: number, name: string) => [
              formatMetricValue(value, series.format),
              name === 'hist' ? `${series.label} (handmatig)` : series.label,
            ]}
          />
          <Area
            type="monotone"
            dataKey="hist"
            stroke="#9ca3af"
            strokeWidth={1.5}
            strokeDasharray="4 3"
            fill="#9ca3af"
            fillOpacity={0.06}
            isAnimationActive={false}
            dot={false}
            connectNulls={false}
          />
          <Area
            type="monotone"
            dataKey="live"
            stroke="var(--brand-accent, #4f46e5)"
            strokeWidth={2}
            fill="var(--brand-accent, #4f46e5)"
            fillOpacity={0.08}
            isAnimationActive={false}
            dot={false}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      {hasHist && (
        <p className="text-xs text-gray-400 mt-1 px-2">
          Gestippeld t/m 2025: handmatige kwartaalcijfers (Excel). Doorgetrokken: live berekend uit eigen data.
        </p>
      )}
    </div>
  );
}
